import { useCallback, useEffect, useRef, useState } from "react";
import { pickRecorderType, uploadWithProgress } from "@unfaithful/client";
import { DIARY, type DiaryUploadRequest, type DiaryUploadTicket, type GameActionInput } from "@unfaithful/shared";

type Stage =
  | { kind: "intro" }
  | { kind: "starting" }
  | { kind: "ready" }
  | { kind: "countdown"; n: number }
  | { kind: "recording"; startedAt: number }
  | { kind: "review"; blob: Blob; url: string; durationMs: number; type: string }
  | { kind: "uploading"; progress: number }
  | { kind: "blocked"; reason: string };

/**
 * THE DIARY ROOM — a 10–15 second confessional recorded on the phone.
 *
 * intro → camera preview → 3·2·1 → recording (hard stop at 15 s) → review
 * → send (or one retake) → upload with progress → "diary-done" with the video.
 * If the camera is refused, players can record voice only, or give a silent
 * "no comment" (which the TV shows as suspicious).
 */
/** Clear speech in a noisy room: the phone's own clean-up for the voice. */
const VOICE: MediaTrackConstraints = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };

export function DiaryRecorder({
  prompt,
  requestUpload,
  act,
}: {
  prompt: string;
  requestUpload: (req: DiaryUploadRequest) => Promise<DiaryUploadTicket | string>;
  act: (action: GameActionInput) => Promise<string | null>;
}) {
  const [stage, setStage] = useState<Stage>({ kind: "intro" });
  const [mode, setMode] = useState<"video" | "audio">("video");
  const [retakes, setRetakes] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const previewRef = useRef<HTMLVideoElement | null>(null);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => () => stopStream(), [stopStream]);

  // Keep the live preview attached to the camera stream.
  useEffect(() => {
    if (previewRef.current && streamRef.current && (stage.kind === "ready" || stage.kind === "countdown" || stage.kind === "recording")) {
      if (previewRef.current.srcObject !== streamRef.current) previewRef.current.srcObject = streamRef.current;
    }
  }, [stage]);

  const openCamera = async (want: "video" | "audio") => {
    setError(null);
    setStage({ kind: "starting" });
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error("unsupported");
      streamRef.current = await navigator.mediaDevices.getUserMedia(
        want === "video"
          ? { video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 1280 }, frameRate: { ideal: 30 } }, audio: VOICE }
          : { audio: VOICE },
      );
      setMode(want);
      setStage({ kind: "ready" });
    } catch (err) {
      const name = (err as Error).name;
      setStage({
        kind: "blocked",
        reason:
          name === "NotAllowedError"
            ? want === "video"
              ? "The camera is blocked. You can record your voice instead, or allow the camera in your browser settings."
              : "The microphone is blocked too. You can still give a silent \"no comment\"."
            : "This phone can't record here. You can still give a silent \"no comment\".",
      });
    }
  };

  const startCountdown = () => {
    let n = 3;
    setStage({ kind: "countdown", n });
    const tick = setInterval(() => {
      n -= 1;
      if (n > 0) setStage({ kind: "countdown", n });
      else {
        clearInterval(tick);
        record();
      }
    }, 800);
  };

  const record = () => {
    const stream = streamRef.current;
    if (!stream) return;
    const type = pickRecorderType(mode);
    const recorder = new MediaRecorder(stream, {
      ...(type ? { mimeType: type } : {}),
      videoBitsPerSecond: 2_500_000,
      audioBitsPerSecond: 128_000,
    });
    chunks.current = [];
    recorder.ondataavailable = (e) => e.data.size > 0 && chunks.current.push(e.data);
    const startedAt = Date.now();
    recorder.onstop = () => {
      const durationMs = Date.now() - startedAt;
      const blobType = recorder.mimeType || type || (mode === "video" ? "video/webm" : "audio/webm");
      const blob = new Blob(chunks.current, { type: blobType });
      setStage({ kind: "review", blob, url: URL.createObjectURL(blob), durationMs, type: blobType });
    };
    recorderRef.current = recorder;
    recorder.start(250);
    setStage({ kind: "recording", startedAt });
    try {
      navigator.vibrate?.(40);
    } catch {
      /* ignore */
    }
  };

  const stop = useCallback(() => {
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  }, []);

  // The recording clock: a visible ring, and a hard stop at 15 seconds.
  useEffect(() => {
    if (stage.kind !== "recording") return;
    const t = setInterval(() => {
      const ms = Date.now() - stage.startedAt;
      setElapsed(ms);
      if (ms >= DIARY.maxMs) stop();
    }, 100);
    return () => clearInterval(t);
  }, [stage, stop]);

  const retake = () => {
    if (stage.kind === "review") URL.revokeObjectURL(stage.url);
    setRetakes((r) => r + 1);
    setElapsed(0);
    setStage({ kind: "ready" });
  };

  const send = async () => {
    if (stage.kind !== "review") return;
    const { blob, durationMs, type, url } = stage;
    setError(null);
    setStage({ kind: "uploading", progress: 0 });
    const ticket = await requestUpload({ contentType: type, bytes: blob.size, durationMs: Math.round(durationMs) });
    if (typeof ticket === "string") {
      setError(ticket);
      setStage({ kind: "review", blob, url, durationMs, type });
      return;
    }
    try {
      // A weak signal shouldn't cost anyone their diary: try three times before giving up.
      for (let attempt = 1; ; attempt++) {
        try {
          await uploadWithProgress(ticket.uploadUrl, blob, ticket.headers, (p) => setStage({ kind: "uploading", progress: p }));
          break;
        } catch (err) {
          if (attempt >= 3) throw err;
          setStage({ kind: "uploading", progress: 0 });
          await new Promise((r) => setTimeout(r, 1500 * attempt));
        }
      }
      const failed = await act({ type: "diary-done", mediaId: ticket.mediaId });
      if (failed) throw new Error(failed);
      URL.revokeObjectURL(url);
      stopStream();
    } catch (err) {
      setError((err as Error).message);
      setStage({ kind: "review", blob, url, durationMs, type });
    }
  };

  const noComment = async () => {
    stopStream();
    const failed = await act({ type: "diary-done", mediaId: null });
    if (failed) setError(failed);
  };

  const live = stage.kind === "ready" || stage.kind === "countdown" || stage.kind === "recording";
  const seconds = Math.min(DIARY.maxMs, elapsed) / 1000;

  return (
    <div className="diary">
      <p className="diary-prompt">{prompt}</p>

      {stage.kind === "intro" && (
        <>
          <p className="quiet">Find a quiet corner. You'll have up to 15 seconds. Only some diaries make it onto the TV.</p>
          <button className="primary wax-button" onClick={() => openCamera("video")}>
            Open the diary room
          </button>
          <button className="text-button" onClick={noComment}>
            Say nothing ("no comment")
          </button>
        </>
      )}

      {stage.kind === "starting" && <p className="quiet">Waking the camera…</p>}

      {live && (
        <div className={`viewfinder ${mode === "audio" ? "is-audio" : ""}`}>
          {mode === "video" ? (
            <video ref={previewRef} className="preview" autoPlay muted playsInline />
          ) : (
            <div className="audio-only" aria-hidden>
              🎙
            </div>
          )}
          {stage.kind === "countdown" && <span className="countdown">{stage.n}</span>}
          {stage.kind === "recording" && (
            <>
              <span className="rec-dot">Recording</span>
              <svg className="ring" viewBox="0 0 100 100" aria-label={`${seconds.toFixed(0)} of 15 seconds`}>
                <circle cx="50" cy="50" r="45" className="ring-track" />
                <circle cx="50" cy="50" r="45" className="ring-fill" style={{ strokeDashoffset: 283 - (283 * seconds) / 15 }} />
                <text x="50" y="58" textAnchor="middle" className="ring-text">
                  {Math.ceil(15 - seconds)}
                </text>
              </svg>
            </>
          )}
        </div>
      )}

      {stage.kind === "ready" && (
        <button className="primary wax-button" onClick={startCountdown}>
          Start recording
        </button>
      )}
      {stage.kind === "recording" && (
        <button className="primary wax-button" onClick={stop} disabled={elapsed < DIARY.minMs}>
          {elapsed < DIARY.minMs ? "Keep talking…" : "Stop"}
        </button>
      )}

      {stage.kind === "review" && (
        <>
          {mode === "video" ? (
            <video className="playback" src={stage.url} controls playsInline />
          ) : (
            <audio className="playback-audio" src={stage.url} controls />
          )}
          <button className="primary wax-button" onClick={send}>
            Send to the castle
          </button>
          {retakes < 1 && (
            <button className="secondary moon-button" onClick={retake}>
              Retake (once)
            </button>
          )}
        </>
      )}

      {stage.kind === "uploading" && (
        <div className="upload" role="status">
          <p className="quiet">Sending your diary… {Math.round(stage.progress * 100)}%</p>
          <div className="bar">
            <span style={{ width: `${Math.round(stage.progress * 100)}%` }} />
          </div>
        </div>
      )}

      {stage.kind === "blocked" && (
        <>
          <p className="notice">{stage.reason}</p>
          {mode === "video" && stage.reason.includes("voice") && (
            <button className="primary wax-button" onClick={() => openCamera("audio")}>
              Record my voice instead
            </button>
          )}
          <button className="secondary moon-button" onClick={noComment}>
            Give a silent "no comment"
          </button>
        </>
      )}

      {error && <p className="error" role="alert">{error}</p>}
    </div>
  );
}
